import { memo, useCallback, useState, type JSX } from 'react'

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import { useColumns, RowActions, NewDialogPanelContent } from 'domains/ContentStores/ExternalStores'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { externalStoresOverviewRoute } from 'pages/NWPlatform/ContentStores/ExternalStores'
import { externalStoreConfigRoute } from 'pages/NWPlatform/ContentStores/ExternalStores/ExternalStore'
import { externalStoreQueryKeys } from 'services/queryKeys'

const OverviewRaw = (): JSX.Element => {
  const { t } = useTranslation(['common', 'domain'])
  const queryClient = useQueryClient()

  const navigate = useNavigate({ from: externalStoresOverviewRoute.fullPath })
  const { environmentId } = externalStoresOverviewRoute.useParams()
  const { query } = externalStoresOverviewRoute.useSearch()

  const [newDialogOpen, setNewDialogOpen] = useState(false)

  const externalStoresQuery = useInfiniteQuery({
    ...externalStoreQueryKeys.list({ environmentId, query }),
    select: (data) => data.pages.flatMap((page) => page.items),
  })

  const columns = useColumns()

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:Integration.searchExternalStores')}
          onSearch={({ search }) => navigate({ search: { query: search } })}
          defaultValue={query}
        />

        <BasicListOperation
          refreshAction={useCallback(
            () => queryClient.invalidateQueries({ queryKey: externalStoreQueryKeys.list({ environmentId }).queryKey }),

            [environmentId, queryClient],
          )}
          createAction={useCallback(() => setNewDialogOpen(true), [])}
        />
      </ActionStrip>

      <Table
        data={externalStoresQuery.data ?? []}
        columns={columns}
        isInitialLoadingGlobal={externalStoresQuery.isLoading}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={externalStoresOverviewRoute.fullPath}
            to={externalStoreConfigRoute.to}
            params={{ externalStoreId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        ActionMenuProps={(row) => ({
          title: row.original.name,
          entity: t('domain:Integration.externalStore', { count: 1 }),
        })}
        RowActions={(props) => <RowActions {...props} />}
      />

      <Dialog
        open={newDialogOpen}
        onClose={() => {
          setNewDialogOpen(false)
        }}
      >
        <NewDialogPanelContent />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
